// Route-level UI that wires everything together (profile/auth user info, tabs, modals), chooses the capture mode (mic vs multi-device), calls the core hooks, and passes state/actions into InterviewTab, TranscriptTab, and AnalysisTab
import {
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
} from "solid-js";
import { SessionState } from "../types/SessionState";
import CaseDetailsModal from "../components/CaseDetailsModal";
import { usePopouts } from "../hooks/usePopouts";
import Spinner from "../components/spinner";
import toast from "solid-toast";
import { useTranscriptionSession } from "../hooks/useTranscriptionSession";
import {
  getSuggestedQuestionsSnapshot,
  useQuestionSuggestions,
  resetSuggestionsHistory,
} from "../hooks/useQuestionsSuggestions";
import { useInsights } from "../hooks/useInsights";
import Layout from "../components/Layout";
import { TabKey } from "../types/ui";
import InterviewBanner from "../components/InterviewBanner";
import SessionHeader from "../components/session/SessionHeader";
import InterviewTab from "../components/session/InterviewTab";
import TranscriptTab from "../components/session/TranscriptTab";
import AnalysisTab from "../components/session/AnalysisTab";
import { useAuth } from "../contexts/AuthContext";
import { processIntakeFile, processIntakeText } from "../api/intakeProcessing";
import { getSession, updateSession, triggerIntakeRanking } from "../api/sessions";
import { getSessionUtterances } from "../api/utterances";
import { extractAnswer } from "../api/extractAnswer";
import { detectQuestions } from "../api/detectQuestions";
import { getCaseById } from "../api/cases";
// import { useDemoPlayback } from "../hooks/useDemoPlayback"; // Demo feature disabled
import type { PriorityTag } from "../types/caseState";
import type { SuggestedQuestion } from "../types/suggestquestions";
import { useSessionBroadcast } from "../hooks/useSessionBroadcast";
import { MicTranscriber } from "../transcription/MicTranscriber";
import { MultiDeviceTranscriber } from "../transcription/MultideviceTranscriber";
import { openSessionChannel } from "../utils/eventBus";
import { API_BASE, withProfile } from "../api/base";

import ReportModal from "../components/ReportModal";
import { generateReport } from "../api/reports";
import {
  refineAiAskedAt,
  buildAnalysisTranscript,
  buildAnswerWindowFromAskedAt,
} from "../features/transcription/transcriptBuilders";
import { useHostStatusHub } from "../features/transcription/useHostStatusHub";
import { useSuggestionsRefresh } from "../features/transcription/useSuggestionsRefresh";
import { useStartWithGeo } from "../features/transcription/useStartWithGeo";
import { useSessionHydration } from "../features/transcription/useSessionHydration";
import { useSessionFinalization } from "../features/transcription/useSessionFinalization";
import { FileTranscriber } from "../transcription/FileTranscriber";
import { SourceTypes } from "../constants/sourceTypes";

interface InterviewSessionPageProps {
  sessionId?: string | null;
  isNewSession?: boolean;
  onNavigateBack?: () => void;
}

/**
 * Upload a file to blob storage and create a session_recordings entry
 */
async function uploadFileToRecordings(file: File): Promise<string> {
  const sessionId = (window as any)._currentSessionId;
  if (!sessionId) {
    throw new Error("No session ID available for file upload");
  }

  console.log(`[uploadFileToRecordings] Starting upload for ${file.name} (${file.size} bytes)`);

  // Determine content type
  const contentType = file.type || (file.name.toLowerCase().endsWith('.wav') ? 'audio/wav' : 'audio/webm');

  // Step 1: Get upload SAS URL with content type hint
  const sasResponse = await fetch(
    withProfile(`${API_BASE}/api/sessions/${sessionId}/recordings`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getUploadSas", contentType }),
    }
  );

  if (!sasResponse.ok) {
    throw new Error(`Failed to get upload SAS: ${sasResponse.status}`);
  }

  const sasData = await sasResponse.json();
  console.log(`[uploadFileToRecordings] Got SAS URL, uploading to blob storage`);

  // Step 2: Upload file to blob storage
  const uploadResponse = await fetch(sasData.uploadUrl, {
    method: "PUT",
    headers: {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": contentType,
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text().catch(() => "");
    throw new Error(`Blob upload failed: ${uploadResponse.status} ${errorText}`);
  }

  console.log(`[uploadFileToRecordings] Blob uploaded successfully`);

  // Step 3: Create recording metadata in database with proper content_type
  const metadataResponse = await fetch(
    withProfile(`${API_BASE}/api/sessions/${sessionId}/recordings`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        blobUrl: sasData.blobUrl,
        startTime: new Date().toISOString(),
        durationSeconds: 0,
        status: "completed",
        fileSizeBytes: file.size,
        mimeType: contentType,
        metadata: {
          uploaded: true,
          fileName: file.name,
          blobName: sasData.blobName,
        },
      }),
    }
  );

  if (!metadataResponse.ok) {
    const errorText = await metadataResponse.text().catch(() => "");
    throw new Error(`Failed to save recording metadata: ${metadataResponse.status} ${errorText}`);
  }

  console.log(`[uploadFileToRecordings] Recording metadata saved to database with content_type: ${contentType}`);
  
  // Return the read URL for playback
  return sasData.readUrl;
}

export default function InterviewSessionPage(props: InterviewSessionPageProps) {
  // Local state signals
  const [activeTab, setActiveTab] = createSignal<TabKey>("interview");
  const [likelihood, setLikelihood] = createSignal<number | undefined>(undefined);
  const [alerts, setAlerts] = createSignal<string[] | undefined>(undefined);
  const [screeningDecision, setScreeningDecision] = createSignal<string | undefined>(undefined);
  const [maltreatment, setMaltreatment] = createSignal<string | undefined>(undefined);
  const [llmPredictionExplanation, setLlmPredictionExplanation] = createSignal<string | undefined>(undefined);
  const [priorityRanking, setPriorityRanking] = createSignal<number | undefined>(undefined);
  const [offenderRole, setOffenderRole] = createSignal<string | undefined>(undefined);
  const [victimType, setVictimType] = createSignal<string | undefined>(undefined);
  const [contactType, setContactType] = createSignal("");
  const [editing, setEditing] = createSignal(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = createSignal(false);
  const [showReport, setShowReport] = createSignal(false);
  const [sessionStatus, setSessionStatus] = createSignal<
    "draft" | "in_progress" | "completed"
  >("draft");
  const [sessionTitle, setSessionTitle] = createSignal<string>(
    `New Session - ${new Date().toLocaleDateString()}`,
  );
  const openModal = () => setEditing(true);
  const closeModal = () => setEditing(false);
  // const [demoMode, setDemoMode] = createSignal(false); // Demo feature disabled
  const [captureMode, setCaptureMode] = createSignal<"mic" | "multi" | "file">("mic");
  const [uploadedFile, setUploadedFile] = createSignal<File | null>(null);
  const [speaker2Lang, setSpeaker2Lang] = createSignal("");
  const [sourceLang, setSourceLang] = createSignal("en-US");
  const [targetLang, setTargetLangUI] = createSignal("");
  const [autoLocation, setAutoLocation] = createSignal("");
  const [caseLocation, setCaseLocation] = createSignal("");
  const [notes, setNotes] = createSignal("");
  const [metadata, setMetadata] = createSignal<any>({});
  const [interpreterModeFlag, setInterpreterModeFlag] = createSignal(false);
  const [isHydrated, setIsHydrated] = createSignal(false);
  const { openCaseworker, openRespondent } = usePopouts(props.sessionId);
  const [isProcessingFile, setProcessingFile] = createSignal(false);
  const [recordingBlobUrl, setRecordingBlobUrl] = createSignal<string | null>(null);
  createEffect(() => {
    // Listen for header event to open report modal
    const onOpen = () => setShowReport(true);
    window.addEventListener("open-report", onOpen as any);
    onCleanup(() => window.removeEventListener("open-report", onOpen as any));
  });

  const getTranscriber = () => {
    const mode = captureMode();
    if (mode === "multi") {
      return new MultiDeviceTranscriber(props.sessionId || "", {
        role: "host",
        captureLocalMic: true,
        speaker1Lang: sourceLang(),
        speaker2Lang: speaker2Lang(),
        onPresence: (p) => {
          try {
            if (p.state === "joined") toast.success("Respondent joined");
            if (p.state === "mic_on") toast("Respondent microphone ON");
            if (p.state === "mic_off") toast("Respondent microphone OFF");
          } catch {}
        },
      });
    }
    if (mode === "file" && uploadedFile()) {
      return new FileTranscriber(uploadedFile()!);
    }
    return new MicTranscriber();
  };

  // Get authenticated user
  const auth = useAuth();
  const currentUser = auth.user();

  // Use logged-in user's name as interviewer name
  const interviewer = () => currentUser?.name || "Unknown User";

  const interpreterConfig = createMemo(() => ({
    enabled: interpreterModeFlag(),
    s1: sourceLang() || "en-US",
    s2: targetLang() || "",
  }));

  const {
    session,
    utterances,
    addUtterance,
    seconds,
    error: transcriptionError,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    startListening,
    stopAll,
    ingestBatch,
    caseState,
    setCaseState,
    setTargetLang: setTargetLangHook,
    markQuestionAsked,
    onAsk,
    waitForAnswer,
    persistInterviewMetadata,
    awaitingAnswer,
    restoreQuestion,
    markQuestionDismissed,
    flushInterviewMetadataNow,
    setPublishUtterance,
  } = useTranscriptionSession(props.sessionId || undefined, {
    getTranscriber,
    interpreterConfig,
  });
  // const demo = useDemoPlayback({
  //   injectUtterance: addUtterance,
  //   onError: (e) => console.error("Demo playback error:", e),
  // });

  const {
    suggestions,
    loading: loadingSuggestions,
    error: questionsError,
    fetchSuggestions,
    cancelSuggestions,
    addBackToSuggestions,
    removeFromSuggestions,
    getAutoMovedOriginal,
    clearAutoMovedOriginal,
    earlier,
    setEarlierFromPersisted,
    replaceSuggestions,
    clearSuggestions,
  } = useQuestionSuggestions(utterances, markQuestionAsked);
  const {
    insights,
    loading: loadingInsights,
    error: insightsError,
    fetchInsights,
    loadSavedInsights,
    summary,
    actionItems,
    toggleAction,
    reset: resetInsights,
  } = useInsights();

  const uiSession = () => {
    // Demo feature disabled: always reflect live session state
    return session();
  };

  useHostStatusHub({
    sessionId: () => props.sessionId || null,
    captureMode,
    sessionState: session,
    workflowStatus: sessionStatus,
  });

  // Surface transcriber errors as toasts for quick diagnosis
  createEffect(() => {
    const err = transcriptionError();
    if (err) {
      toast.error(`Transcription error: ${err}`);
      console.error("Transcription error:", err);
    }
  });
  // Session hydration is handled by useSessionHydration (feature hook).

  const speechLang = () => [sourceLang()];
  const displayLangs = () => [sourceLang(), targetLang() || ""].filter(Boolean);
  const [popupMsg, setPopupMsg] = createSignal<string | null>(null);

  const buildEnglishTranscriptFrom = (uts: ReturnType<typeof utterances>) =>
    buildAnalysisTranscript(uts, interpreterModeFlag());
  const buildEnglishWindowForGrounding = (
    askedAtIso?: string | number | Date,
    hardCapMs: number = 45_000,
  ) =>
    buildAnswerWindowFromAskedAt(
      utterances(),
      askedAtIso,
      interpreterModeFlag(),
      hardCapMs,
    );
  const anchorAskedAt = (
    questionText: string,
    approxAskedAt?: string | number | Date,
  ) => refineAiAskedAt(utterances(), questionText, approxAskedAt);

  // Handler to regenerate meeting insights
  const handleRegenerateInsights = () => {
    const transcript = buildEnglishTranscriptFrom(utterances());
    if (!transcript.trim()) {
      toast.error("No transcript available to generate insights");
      return;
    }
    fetchInsights(
      transcript,
      contactType() || "Intake",
      props.sessionId || undefined,
    );
  };

  const isIdle = () => session() === SessionState.Idle;
  const isConnected = () => session() === SessionState.Connected;
  const isListening = () => session() === SessionState.Listening;

  const [respondent, setRespondent] = createSignal("");
  const [caseNumber, setCaseNumber] = createSignal("");
  const [showValidation, setShowValidation] = createSignal(false);
  const { startWithGeo } = useStartWithGeo({
    sessionId: () => props.sessionId || null,
    contactType,
    respondentName: respondent,
    sourceLang,
    targetLang: () => targetLang() || "",
    setShowValidation,
    setAutoLocation,
    startListening,
    resetSuggestionsHistory,
    clearSuggestions,
    resetInsights,
    getSession,
  });

  useSessionHydration({
    sessionId: () => props.sessionId || null,
    isNewSession: () => Boolean(props.isNewSession),
    getSession,
    getCaseById,
    getSessionUtterances,
    getSessionInsights: async (id) => {
      const mod = await import("../api/insights");
      return mod.getSessionInsights(id);
    },
    setIsReadOnlyMode,
    setSessionStatus,
    setSessionTitle,
    setContactType,
    setRespondent,
    setCaseNumber,
    setAutoLocation,
    setStartTime,
    setEndTime,
    setCaseState,
    ingestBatch,
    loadSavedInsights,
    replaceSuggestions,
    setEarlierFromPersisted,
    setSourceLang,
    setTargetLangUi: setTargetLangUI,
    setTargetLangHook,
    setInterpreterModeFlag,
    setCaptureMode,
    setSpeaker2Lang,
    setMetadata,
    onHydrated: () => setIsHydrated(true),
  });

  const handleTranscriptionToggle = async () => {
    console.log("InterviewSessionPage.tsx handleTranscriptionToggle", session(), captureMode(), !!uploadedFile());
    (window as any)._transcriptionStartTime = Date.now();
    console.log(`[Timer] Transcription initiation started at: ${new Date((window as any)._transcriptionStartTime).toISOString()}`);
    switch (session()) {
      case SessionState.Idle:
          // If we have an uploaded file, start processing immediately
          if (captureMode() === "file" && uploadedFile()) {
            console.log("InterviewSessionPage.tsx handleTranscriptionToggle - starting file listening");
            await startListening(sourceLang(), targetLang() || null);
          } else {
            console.log("InterviewSessionPage.tsx handleTranscriptionToggle - starting with geo");
            await startWithGeo();
          }
          break;

      case SessionState.Listening:
        stopAll({ keepCallAlive: true });
        break;

      default:
        await startListening(sourceLang(), targetLang() || null);
        break;
    }
  };

  const { publishUtterance } = useSessionBroadcast({
    sessionId: props.sessionId || null,
    uiSession,
    sessionStatus,
    interpreterEnabled: interpreterModeFlag,
    sourceLang,
    targetLang: () => targetLang() || "",
  });
  createEffect(() => {
    setPublishUtterance(publishUtterance);
  });

  createEffect(() => {
    if (props.sessionId && isHydrated()) {
      console.log("InterviewSessionPage.tsx createEffect - Hydrated, checking for pending file");
      (window as any)._currentSessionId = props.sessionId; // Store for upload helper
      const pendingFile = (window as any)._pendingUploadFile;
      if (pendingFile) {
        setUploadedFile(pendingFile);
        setCaptureMode("file");
        (window as any)._captureMode = "file"; // Global flag for hooks
        delete (window as any)._pendingUploadFile;

        setProcessingFile(true);
        hasTriggeredSuggestions = false; // Reset flag for new file
        
        // Update session source_type to wav_file for uploaded WAV files
        if (pendingFile.name.toLowerCase().endsWith('.wav')) {
          updateSession(props.sessionId, { source_type: SourceTypes.WAV_FILE })
            .then(() => console.log("Session source_type set to wav_file"))
            .catch((err) => console.error("Failed to set source_type:", err));
        }
      
        // Upload the WAV file to blob storage before processing
        uploadFileToRecordings(pendingFile)
          .then((blobUrl) => {
            console.log("WAV file uploaded to session_recordings");
            setRecordingBlobUrl(blobUrl);
            handleTranscriptionToggle();
          })
          .catch((err) => {
            console.error("Failed to upload WAV file:", err);
            toast.error("Failed to upload file to storage");
            // Continue with transcription even if upload fails
            handleTranscriptionToggle();
          });
      }
    }
  });

  const finalization = useSessionFinalization({
    sessionId: () => props.sessionId || null,
    sourceLang: sourceLang,
    likelihood: likelihood,
    setLikelihood,
    setScreeningDecision,
    setMaltreatment,
    setPriorityRanking,
    setLlmPredictionExplanation,
    setAlerts,
    setOffenderRole,
    setVictimType,
    contactType,
    interpreterEnabled: interpreterModeFlag,
    utterances,
    caseState,
    setCaseState,
    persistInterviewMetadata,
    stopAll: () => stopAll(),
    setWorkflowStatus: setSessionStatus,
    setReadOnly: setIsReadOnlyMode,
    setPopupMsg,
    buildEnglishTranscript: (uts) => buildEnglishTranscriptFrom(uts as any),
    buildEnglishWindowFromAskedAt: (askedAt, hardCapMs) =>
      buildEnglishWindowForGrounding(askedAt, hardCapMs),
    refineAiAskedAt: anchorAskedAt,
    extractAnswer,
    detectQuestions,
    fetchInsights,
    generateReport,
    updateSession,
  });

  const [isFinalizing, setIsFinalizing] = createSignal(false);

  // Track if we've already triggered suggestions for this file processing session
  let hasTriggeredSuggestions = false;

  createEffect(() => {
  const isFile = captureMode() === "file";
  const isDoneTranscribing = session() === SessionState.Idle;
  const hasUtterances = utterances().length > 0;
  
  // When file transcription completes, trigger suggestions fetch
  if (isFile && isDoneTranscribing && hasUtterances && !hasTriggeredSuggestions) {
    hasTriggeredSuggestions = true;
    console.log("[File Mode] Transcription complete. Triggering suggestions fetch...");
    const transcript = buildEnglishTranscriptFrom(utterances() as any);
    fetchSuggestions(transcript, caseState(), contactType());
  }
});

  createEffect(() => {
  const isFile = captureMode() === "file";
  const isDoneTranscribing = session() === SessionState.Idle;
  const isDoneAI = !loadingSuggestions(); // Wait for AI to finish
  const hasUtterances = utterances().length > 0;
  const notSavedYet = sessionStatus() !== "completed";

  // log all items that
  if (isFile && isDoneTranscribing && isDoneAI && hasUtterances && notSavedYet) {
    console.log("[Auto-Stop] Audio finished and AI finished. Saving to DB...");
    handleStopUnified();
    setProcessingFile(false);
  }
});

  async function handleStop() {
    // 1) Run finalization first — backfilling may answer remaining questions
    await finalization.finalizeSession();

    // 2) After finalization: move unanswered suggestions to "earlier"
    try {
      const currentSuggestions = suggestions();
      const earlierSuggestions = earlier();

      // Filter out questions that were answered during backfilling
      const answeredSet = new Set(
        (caseState().answeredQuestions || [])
          .filter((qa: any) => qa.answer && String(qa.answer).trim())
          .map((qa: any) => (qa.question || "").trim().toLowerCase()),
      );
      const remainingSuggestions = currentSuggestions.filter(
        (q) => !answeredSet.has((q.question || "").trim().toLowerCase()),
      );

      // Merge remaining suggestions into earlier (dedupe by question text)
      const mergedEarlier = (() => {

        const merged = [...earlierSuggestions, ...remainingSuggestions];
        const seen = new Set<string>();
        return merged.filter((item) => {
          const key = (item.id ?? item.question ?? "").trim().toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      })();

      // 3) Update UI signals immediately so the user sees the change (for file mode, this isn't needed)
      if (captureMode() !== "file") {
        setEarlierFromPersisted(mergedEarlier);
        clearSuggestions();
      }

      console.log("InterviewSessionPage.tsx handleStop2 - cleared suggestions");
      console.log("InterviewSessionPage.tsx handleStop2 - mergedEarlier", mergedEarlier);
      console.log("InterviewSessionPage.tsx handleStop2 - currentSuggestions", currentSuggestions);
      // 4) Flush to DB immediately (non-debounced) so refresh sees correct state
      await flushInterviewMetadataNow({
        suggestedQuestions: [],
        earlierSuggestions: mergedEarlier,
      });
    } catch (e) {
      console.warn("Failed to persist suggestions on stop:", e);
    }
  }

  const handleStartPauseResumeUnified = async () => {
    // Demo feature disabled: route to live transcription controls only
    await handleTranscriptionToggle();
  };

  const handleStopUnified = async () => {
    // Demo feature disabled: stop live session only
    setPopupMsg("Finalizing session. Generating insights and report");
    try {
      await handleStop();
    } finally {
      setPopupMsg(null);
      (window as any)._captureMode = null; // Ensure flag is cleared
    }
  };

  // Broadcast an "ended" signal if the main page is closed or navigated away
  // so any pop-outs/guest can react (e.g., stop mic, show ended state).
  const sendEnded = () => {
    try {
      const id = props.sessionId || null;
      if (!id) return;
      const bc = openSessionChannel(id);
      bc.postMessage({ type: "status", status: "ended" });
      bc.close();
    } catch {}
  };

  // beforeunload and component cleanup
  const onBeforeUnload = () => sendEnded();
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", onBeforeUnload);
  }
  onCleanup(() => {
    try {
      sendEnded();
      if (typeof window !== "undefined")
        window.removeEventListener("beforeunload", onBeforeUnload);
    } catch {}
  });

  const banner = (
    <InterviewBanner
      sourceLang={sourceLang()}
      targetLang={targetLang()}
      uploadedFile={uploadedFile()!}
      recordingBlobUrl={recordingBlobUrl()}
      // demoMode={demoMode()}
      // onToggleDemo={setDemoMode}
      // scenarios={scenarios}
      // selectedScenarioId={scenarioId()}
      session={uiSession()}
      onStop={handleStopUnified}
      // onSelectScenario={setScenarioId}
      captureMode={captureMode()}
      speaker2Lang={speaker2Lang()}
      onSource={(v) => {
        setSourceLang(v);
        if (props.sessionId) {
          updateSession(props.sessionId, {
            source_language: v || undefined,
          })
            .then(() => {
              console.log("Saved source language to session:", v);
            })
            .catch((error) => {
              console.error("Failed to save source language:", error);
            });
        }
      }}
      onTarget={(v) => {
        setTargetLangUI(v);
        setTargetLangHook(v);
        if (props.sessionId) {
          updateSession(props.sessionId, {
            target_language: v || undefined,
          })
            .then(() => {
              console.log("Saved target language to session:", v);
            })
            .catch((error) => {
              console.error("Failed to save target language:", error);
            });
        }
      }}
      contactType={contactType()}
      onContactType={(v) => {
        setContactType(v);
        if (v) setShowValidation(false);
        if (props.sessionId) {
          updateSession(props.sessionId, {
            contact_type: v,
          })
            .then(() => {
              console.log("Saved contact type to session:", v);
            })
            .catch((error) => {
              console.error("Failed to save contact type:", error);
            });
        }
      }}
      interpreterMode={interpreterModeFlag()}
      onOpenInterviewerView={() => {
        try {
          openCaseworker();
        } catch (e) {
          console.error("Failed to open interviewer pop-out:", e);
        }
      }}
      onOpenRespondentView={() => {
        try {
          openRespondent();
        } catch (e) {
          console.error("Failed to open respondent pop-out:", e);
        }
      }}
      onStartPauseResume={handleStartPauseResumeUnified}
      seconds={seconds()}
      isReadOnly={isReadOnlyMode()}
      utteranceCount={utterances().length}
      sessionStatus={sessionStatus()}
      interviewer={interviewer()}
      respondent={respondent()}
      onRespondent={(v) => {
        setRespondent(v);
        // Seed reporterName from respondent for slot-seeding and report generation
        if (v) {
          setCaseState((current) => ({
            ...current,
            reporterName: current.reporterName || v,
          }));
        }
        if (v) setShowValidation(false);
        if (props.sessionId) {
          updateSession(props.sessionId, { respondent_name: v }).catch(
            (error) => {
              console.error("Failed to save respondent:", error);
            },
          );
        }
      }}
      activeTab={activeTab}
      setSourceLang={setSourceLang}
      setTargetLang={setTargetLangUI}
      onStart={startWithGeo}
      onNavigateBack={props.onNavigateBack}
      showValidation={showValidation()}
      sessionId={props.sessionId || ""}
      onRecordingCompleted={(blobUrl) => {
        console.log("[InterviewSessionPage] Recording completed, blob URL:", blobUrl);
        setRecordingBlobUrl(blobUrl);
      }}
    />
  );

  createEffect(() => {
    if (props.sessionId) {
      if (props.isNewSession) {
        // New session - always editable
        console.log("Starting new session:", props.sessionId);
        setIsReadOnlyMode(false);
        setSessionStatus("draft");
      } else {
        // Existing session - check actual status from database
        console.log("Loading existing session:", props.sessionId);
        // Clear stale suggestion history from previous session
        resetSuggestionsHistory();

        getSession(props.sessionId)
          .then((sessionData) => {
            // Set read-only based on session status and reflect status in UI
            const isReadOnly = sessionData.status === "completed";
            setIsReadOnlyMode(isReadOnly);
            setSessionStatus(
              (sessionData.status as any) ||
                (isReadOnly ? "completed" : "draft"),
            );

            if (sessionData.likelihood) {
              console.log("sessionData interviewsessionpage.tsx", sessionData);
              console.log("sessionData.likelihood interviewsessionpage.tsx", sessionData.likelihood);
              setLikelihood(sessionData.likelihood);
              setAlerts(sessionData.alarms?.split(",") || []);
              //setNarrative(sessionData.narrative);
              setPriorityRanking(sessionData.priorityRanking);
              setScreeningDecision(sessionData.screeningDecision);
              setMaltreatment(sessionData.maltreatment);
              setLlmPredictionExplanation(sessionData.llmPredictionExplanation);
            }

            setSessionTitle(sessionData.title);

            // Load session data into the transcription interface
            setContactType(sessionData.contactType || "");

            // Load respondent, interviewer, and case info
            if (sessionData.respondentName) {
              setRespondent(sessionData.respondentName);
            }
            if (sessionData.interviewerName) {
            }

            // Load case number and auto-location by fetching case data
            if (sessionData.caseId) {
              getCaseById(sessionData.caseId)
                .then((caseData) => {
                  setCaseNumber(caseData.case_number || "");
                  // Load auto-location from case metadata
                  if (
                    caseData.metadata &&
                    (caseData.metadata as any).autoLocation
                  ) {
                    setAutoLocation((caseData.metadata as any).autoLocation);
                  }
                })
                .catch((err) => {
                  console.error("Failed to load case data:", err);
                  setCaseNumber(""); // Fallback to empty
                });
            } else {
              setCaseNumber(""); // No case associated
            }

            // Restore timing data from database
            if (sessionData.startTime) {
              const startDate = new Date(sessionData.startTime);
              setStartTime(startDate);
              console.log("Restored start time:", startDate);
            }
            if (sessionData.endTime) {
              const endDate = new Date(sessionData.endTime);
              setEndTime(endDate);
              console.log("Restored end time:", endDate);
            }

            // Restore interview questions from metadata

            const metaAny: any = sessionData.metadata as any;
            if (metaAny && metaAny.interviewQuestions) {
              const interviewQuestions = metaAny.interviewQuestions;
              console.log(
                " Restoring interview questions:",
                interviewQuestions,
              );

              // Restore the interview state
              setCaseState((current) => ({
                ...current,
                askedQuestions: interviewQuestions.asked || [],
                answeredQuestions: (interviewQuestions.answered || []).map(
                  (qa: any) => ({
                    question: qa.question,
                    fieldKey: qa.fieldKey,
                    answer: qa.answer,
                    answer_en: qa.answer_en,
                    paraphrase: qa.paraphrase,
                    quote: qa.quote,
                    priority_level: qa.priority_level,
                    source: qa.source,
                    origin: qa.origin,
                    confidence: qa.confidence,
                    method: qa.method,
                    answerStatus: qa.answerStatus,
                    askedAt: qa.askedAt,
                    answeredAt: qa.answeredAt,
                  }),
                ),
                dismissedQuestions: interviewQuestions.dismissed || [],
              }));
            } else {
              console.log("No interview questions found in metadata");
            }

            // Restore suggestions and earlier from metadata, filtered against asked/answered/dismissed
            const saved = sessionData.metadata?.interviewQuestions?.suggested;
            const savedEarlier =
              sessionData.metadata?.interviewQuestions?.earlier;
            const askedSet = new Set(
              (sessionData.metadata?.interviewQuestions?.asked || []).map(
                (q: string) => (q || "").trim().toLowerCase(),
              ),
            );
            const answeredSet = new Set(
              (sessionData.metadata?.interviewQuestions?.answered || []).map(
                (qa: any) => (qa.question || "").trim().toLowerCase(),
              ),
            );
            const dismissedSet = new Set(
              (sessionData.metadata?.interviewQuestions?.dismissed || []).map(
                (d: any) =>
                  (typeof d === "string" ? d : d.question || "")
                    .trim()
                    .toLowerCase(),
              ),
            );

            const toSQ = (s: any): SuggestedQuestion =>
              typeof s === "string"
                ? {
                    id: s,
                    question: s,
                    priority_level: "Informational" as PriorityTag,
                    importance: "",
                    fieldKey: null,
                  }
                : {
                    id: s.id ?? s.question,
                    question: s.question,
                    priority_level: (s.priority_level ??
                      "Informational") as PriorityTag,
                    importance: s.importance ?? "",
                    dismissed: (s as any).dismissed ?? false,
                    fieldKey: (s as any).fieldKey ?? null,
                    source: (s as any).source ?? undefined,
                  };

            const earlierArr = Array.isArray(savedEarlier)
              ? savedEarlier.map(toSQ)
              : [];
            const earlierSet = new Set(
              earlierArr.map((e) => (e.question || "").trim().toLowerCase()),
            );

            const filteredSuggested = Array.isArray(saved)
              ? (saved as any[]).map(toSQ).filter((q) => {
                  const key = (q.question || "").trim().toLowerCase();
                  return (
                    !askedSet.has(key) &&
                    !answeredSet.has(key) &&
                    !dismissedSet.has(key) &&
                    !earlierSet.has(key)
                  );
                })
              : [];

            // Policy: if session is completed (read-only), show saved top snapshot (deduped).
            // Otherwise, skip restoring saved suggestions; let backend repopulate live list.
            const isCompleted = sessionData.status === "completed";
            if (isCompleted) {
              replaceSuggestions(filteredSuggested);
            } else {
              replaceSuggestions([]);
            }

            setEarlierFromPersisted(earlierArr);

            // Restore language settings from saved session
            if (sessionData.sourceLanguage) {
              setSourceLang(sessionData.sourceLanguage);
            }
            if (sessionData.targetLanguage) {
              setTargetLangUI(sessionData.targetLanguage);
              setTargetLangHook(sessionData.targetLanguage);
            }

            // Fetch recording blob URL if session has a recording
            if (sessionData.hasRecording) {
              console.log("Session has recording, fetching blob URL...");
              fetch(withProfile(`${API_BASE}/api/sessions/${props.sessionId}/recordings`))
                .then((res) => {
                  if (!res.ok) throw new Error(`Failed to fetch recordings: ${res.status}`);
                  return res.json();
                })
                .then((data) => {
                  if (data.recordings && data.recordings.length > 0) {
                    const recording = data.recordings[0];
                    console.log("Recording blob URL loaded:", recording.blob_url);
                    setRecordingBlobUrl(recording.blob_url);
                    // Set capture mode to file ONLY if this is a wav_file source type
                    // For live recordings (mic/multi), keep the original capture mode
                    if (sessionData.sourceType === "wav_file") {
                      setCaptureMode("file");
                    }
                  }
                })
                .catch((err) => {
                  console.error("Failed to fetch recording blob URL:", err);
                });
            }

            // Load saved utterances and insights in parallel and hydrate immediately from saved insights
            const uttPromise = getSessionUtterances(props.sessionId!);
            const insPromise = import("../api/insights").then((m) =>
              m.getSessionInsights(props.sessionId!),
            );

            Promise.allSettled([uttPromise, insPromise])
              .then(([uttRes, insRes]) => {
                if (insRes.status === "fulfilled") {
                  const insightsData = insRes.value;
                  const meetingInsights = (insightsData.insights || [])
                    .filter((i: any) => i.type === "meeting_insights")
                    .sort(
                      (a: any, b: any) =>
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime(),
                    )[0];
                  if (meetingInsights?.data) {
                    loadSavedInsights(meetingInsights.data);
                  }
                }

                if (uttRes.status === "fulfilled") {
                  const utteranceData = uttRes.value;
                  console.log(
                    `Loaded ${utteranceData.utterances.length} saved utterances`,
                  );
                  const savedUtterances = utteranceData.utterances.map((u) => ({
                    id: u.id,
                    speaker: u.speaker,
                    text: u.text,
                    start: u.startTime,
                    end: u.endTime,
                    lang: u.language,
                    translation: u.translation,
                    confidence: u.confidence,
                  }));
                  ingestBatch(
                    savedUtterances,
                    savedUtterances.map((u) => u.text).join(" "),
                  );
                }
              })
              .catch((error) => {
                console.error("Failed to load saved data:", error);
              });
          })
          .catch((error) => {
            console.error("Failed to load session:", error);
            // On error, default to read-only for safety
            setIsReadOnlyMode(true);
            setSessionStatus("completed");
          });
      }
    } else {
      setIsReadOnlyMode(false);
      setSessionStatus("draft");
    }
  });

  // Keep status in sync with live session transitions
  createEffect(() => {
    if (sessionStatus() === "completed") return;
    if (
      uiSession() === SessionState.Listening ||
      uiSession() === SessionState.Connected
    ) {
      setSessionStatus("in_progress");
    } else if (uiSession() === SessionState.Idle && utterances().length === 0) {
      setSessionStatus("draft");
    }
  });

  createEffect(() => {
    if (props.sessionId && currentUser) {
      updateSession(props.sessionId, { interviewer_name: currentUser.name })
        .then(() => {
          console.log(
            "Automatically saved interviewer name to session:",
            currentUser.name,
          );
        })
        .catch((error) => {
          console.error("Failed to save interviewer name to session:", error);
        });
    }
  });

  useSuggestionsRefresh({
    sessionState: uiSession,
    seconds,
    utterances,
    awaitingAnswer,
    contactType,
    caseState,
    buildAnalysisTranscript: (uts) => buildEnglishTranscriptFrom(uts as any),
    fetchSuggestions,
  });

  // Navigation warning
  createEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uiSession() !== SessionState.Idle) {
        e.preventDefault();
        e.returnValue =
          "Recording in progress. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  });

  return (
    <div class="h-screen overflow-hidden flex flex-col">
      <Show when={isProcessingFile()}>
        <div class="fixed inset-0 bg-white/70 grid place-items-center z-50 pointer-events-none">
          <Spinner />
        </div>
      </Show>
      <SessionHeader
        title={
          <Show when={props.sessionId} fallback="IntelliCoach">
            {sessionTitle()}
          </Show>
        }
        onNavigateBack={props.onNavigateBack}
      />
      <div class="flex-1 min-h-0">
        <Layout
          active={activeTab()}
          onSelect={setActiveTab}
          onCaseInfoClick={openModal}
          banner={banner}
        >
          {/* transcript panels */}

          <Show when={activeTab() === "interview"}>
            <InterviewTab
              sessionId={props.sessionId || null}
              interpreterEnabled={interpreterModeFlag()}
              suggestions={suggestions()}
              asked={caseState().askedQuestions}
              dismissed={(caseState().dismissedQuestions || []) as any}
              earlier={earlier()}
              contactType={contactType()}
              readOnly={isReadOnlyMode()}
              caseState={caseState()}
              setCaseState={setCaseState}
              onAsk={onAsk}
              onDismiss={markQuestionDismissed}
              onRestore={restoreQuestion}
              likelihood={likelihood()}
              screeningDecision={screeningDecision()}
              maltreatment={maltreatment()}
              llmPredictionExplanation={llmPredictionExplanation()}
              priorityRanking={priorityRanking()}
              alerts={alerts() || []}
              offenderRole={offenderRole()}
              victimType={victimType()}
              onDismissEarlier={(q) => {
                try {
                  setEarlierFromPersisted(
                    (earlier() || []).filter(
                      (it) => (it.id ?? it.question) !== (q.id ?? q.question),
                    ),
                  );
                } catch {}
              }}
              removeFromSuggestions={removeFromSuggestions}
              addBackToSuggestions={addBackToSuggestions}
              setEarlierFromPersisted={setEarlierFromPersisted}
              persistInterviewMetadata={persistInterviewMetadata}
            />
          </Show>
          <Show when={activeTab() === "transcript"}>
            <TranscriptTab
              interpreterMode={interpreterModeFlag()}
              utterances={utterances()}
              insights={insights()}
              targetLang={targetLang()}
              sourceLang={sourceLang()}
            />
          </Show>
          <Show when={activeTab() === "analysis"}>
            <AnalysisTab
              summary={summary()}
              insights={insights()}
              loading={loadingInsights()}
              error={insightsError()}
              actionItems={actionItems()}
              onToggleAction={toggleAction}
              onRegenerate={handleRegenerateInsights}
            />
          </Show>

          <Show when={session() === SessionState.Stopping}>
            <div class="fixed inset-0 bg-white/70 grid place-items-center z-50 pointer-events-none">
              <Spinner />
            </div>
          </Show>
          {/* //<Toaster position="top-right" gutter={8} /> */}
          <CaseDetailsModal
            open={editing()}
            onClose={closeModal}
            startTime={startTime()}
            endTime={endTime()}
            autoLocation={autoLocation()}
            caseLocation={caseLocation()}
            setCaseLocation={setCaseLocation}
            contactType={contactType()}
            setContactType={setContactType}
            notes={notes()}
            setNotes={setNotes}
          />
        </Layout>
      </div>
      <Show when={!!popupMsg()}>
        <div class="fixed inset-0 bg-black/30 z-[10001] grid place-items-center">
          <div class="bg-white rounded-md shadow p-4 text-sm text-gray-700 flex items-center gap-3">
            <Spinner />
            <span>{popupMsg()}</span>
          </div>
        </div>
      </Show>
      <ReportModal
        sessionId={props.sessionId || ""}
        contactType={contactType() || "Intake"}
        isOpen={showReport()}
        onClose={() => setShowReport(false)}
      />
    </div>
  );
}
